import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { Button, Checkbox, Divider, Form, Input } from 'antd';
import { Link } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { useIntl } from 'react-intl';

import IntlMessages from 'util/IntlMessages';
import { hideAuthLoader, showAuthLoader } from 'appRedux/actions/Auth';
import { errorNotification } from 'appRedux/actions';
import { auth } from '@src/firebase/firebase';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';

import authStyles from '../authShared.module.less';

import SocialSignIn from '../../components/SocialSignIn';
import Card from '../../components/Card';
import AppBrand from 'components/AppBrand';

const SignUp = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { authUser, loader } = useSelector(({ auth }) => auth);
  const isUserIn = !!authUser;
  const disableInputs = loader || isUserIn;

  const handleFinish = async values => {
    dispatch(showAuthLoader());

    try {
      const signUpFn = apiCallable(API_OPERATIONS.SIGN_UP);
      const response = await signUpFn(values);
      const userId = response?.data?.uid || response?.data?.userId;
      const customToken = response?.data?.customToken;

      if (userId) localStorage.setItem('user_id', userId);
      if (customToken) await signInWithCustomToken(auth, customToken);

      window.location.href = '/onboarding';
    } catch (error) {
      dispatch(errorNotification(error?.message || 'Error creating user'));
    } finally {
      dispatch(hideAuthLoader());
    }
  };

  return (
    <Card
      footer={
        <div
          className={classnames(
            'gx-flex-column gx-justify-content-center gx-align-items-center gx-w-100',
            authStyles.footer,
          )}
        >
          <div>
            <IntlMessages id="auth.signUp.alreadyHaveAccount" />{' '}
            <Link className={authStyles.footerLink} to="/signin">
              <IntlMessages id="app.userAuth.signIn" />
            </Link>
          </div>
        </div>
      }
      className={authStyles.card}
    >
      <main className={authStyles.main} id="signup-main" aria-labelledby="app-brand-title">
        <AppBrand authHero className={authStyles.appBrand} titleAs="h1">
          <IntlMessages id="auth.signUp.title" />
        </AppBrand>
        <SocialSignIn disabled={disableInputs} />
        <Divider className={authStyles.divider}>
          <IntlMessages id="auth.socialSignIn.or" />
        </Divider>
        <Form
          initialValues={{
            acceptedTerms: null,
            email: '',
            password: '',
          }}
          layout="vertical"
          requiredMark
          name="basic"
          onFinish={handleFinish}
          className={classnames('gx-signin-form gx-form-row0', authStyles.form)}
        >
          <Form.Item
            label={intl.formatMessage({ id: 'appModule.email' })}
            name="email"
            rules={[
              {
                type: 'email',
                message: intl.formatMessage({ id: 'form.format.msg' }),
              },
              { required: true, message: intl.formatMessage({ id: 'form.required.msg' }) },
            ]}
          >
            <Input autoComplete="username" disabled={disableInputs} />
          </Form.Item>
          <Form.Item
            label={intl.formatMessage({ id: 'appModule.password' })}
            name="password"
            rules={[{ required: true, message: intl.formatMessage({ id: 'form.required.msg' }) }]}
          >
            <Input.Password autoComplete="current-password" disabled={disableInputs} />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              className={classnames('gx-mb-0', authStyles.submitButton, isUserIn && 'gx-btn-cyan')}
              htmlType="submit"
              loading={disableInputs}
              disabled={disableInputs}
            >
              <IntlMessages id={isUserIn ? 'app.userAuth.signUpDone' : 'app.userAuth.signUp'} />
            </Button>
          </Form.Item>
          <div className={authStyles.subpanel}>
            <Form.Item
              name="acceptedTerms"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error(intl.formatMessage({ id: 'form.required.msg' }))),
                },
              ]}
            >
              <Checkbox disabled={disableInputs}>
                <IntlMessages id="auth.signUp.iAccept" />{' '}
                <Link
                  className={classnames('gx-login-form-forgot', authStyles.touchLink)}
                  to="/terms_and_conditions"
                >
                  <IntlMessages id="auth.signUp.termsAndConditions" />
                </Link>
              </Checkbox>
            </Form.Item>
          </div>
        </Form>
      </main>
    </Card>
  );
};

export default SignUp;
